import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import DataTable from "../ui/DataTable";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from "@mui/material";
import FormDialog from "./dialog";
import { useNavigate } from "react-router-dom";
import AnalyticsDialog from "./AnalyticsDialog";
import ReportDialog from "./ReportDialog";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { Target, Award, Flag, Download, FileSpreadsheet, FileText } from "lucide-react";
import PageActionsMenu, {
  ActionsMenuItem,
  ActionsSection,
} from "../ui/PageActionsMenu";
import { useTheme } from "styled-components";
import { useCourseFilter } from "../context/CourseFilterContext";
import { buildGradeSummary, toInitials } from "./gradeSummary";
import {
  buildScoresListUrl,
  getNextScoresSort,
  sortScoresLocally,
  SCORES_LIST_DEFAULTS,
  SCORES_SORT_FIELDS,
} from "./scoreListApi";
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';
import {
  SCORES_FETCH_TIMEOUT_MS,
  SCORE_LIST_MESSAGES,
  getScoreListFetchErrorMessage,
  parseScoresListResponse,
} from "./scoreListMessages";
import {
  GradeSummaryShimmer,
  SCORE_LOADING_TABLE_COLUMNS,
} from "./scoreLoadingShimmer";
import { LabelWithHelp } from "../ui/FieldHelp";
import { GRADE_CENTER_KPI_HELP } from "../ui/kpiFieldHelp";
import {
  StatGrid,
  Stat,
  StatTop,
  StatIco,
  StatLabel,
  StatValue,
  ChartGrid,
  Card,
  Tag,
  Av,
  SectionHead,
  Chip,
  CellPrimary,
  CellSub,
  SetTabs,
  SetTab,
} from "../ui/primitives";
import AssessmentDashboard from "../dashboard/assessmentDashboard";

const initialValue = { role: "", percentage: "" };

const PageShell = styled.div`
  min-height: calc(100vh - 32px);
  margin: 0 auto;
  padding: 0 28px 36px;
  color: ${({ theme }) => theme.text};
  font-family: ${({ theme }) => theme.fontBody};

  @media (max-width: 768px) {
    padding: 0 14px 28px;
  }
`;

const ContentWrap = styled.div`
  width: min(1180px, 100%);
  margin: 0 auto;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin: 0 0 ${({ theme }) => theme.gap};
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const TableWrap = styled.div`
  width: 100%;
`;

const LEADER_COLORS = [
  "#5b54e6",
  "#2f74e0",
  "#10977a",
  "#e0922f",
  "#8b3ff0",
  "#d6455d",
];

function GradeSummary({ summary }) {
  const theme = useTheme();
  if (!summary) return null;

  const bandColor = {
    success: theme.success,
    accent: theme.accent,
    warning: theme.warning,
    danger: theme.danger,
  };
  const headStyle = {
    fontFamily: theme.fontHead,
    fontSize: 16,
    fontWeight: 700,
    margin: "0 0 16px",
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 16,
        marginBottom: 16,
      }}
    >
      <StatGrid style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
        <Stat>
          <StatTop>
            <StatIco>
              <Target size={19} />
            </StatIco>
          </StatTop>
          <StatLabel><LabelWithHelp help={GRADE_CENTER_KPI_HELP.averageScore}>Average score</LabelWithHelp></StatLabel>
          <StatValue className="tnum">{summary.avg}%</StatValue>
        </Stat>
        <Stat>
          <StatTop>
            <StatIco>
              <Award size={19} />
            </StatIco>
          </StatTop>
          <StatLabel><LabelWithHelp help={GRADE_CENTER_KPI_HELP.passRate}>Pass rate</LabelWithHelp></StatLabel>
          <StatValue className="tnum">{summary.passRate}%</StatValue>
        </Stat>
        <Stat>
          <StatTop>
            <StatIco>
              <Flag size={19} />
            </StatIco>
          </StatTop>
          <StatLabel><LabelWithHelp help={GRADE_CENTER_KPI_HELP.topScore}>Top score</LabelWithHelp></StatLabel>
          <StatValue className="tnum">{summary.top}%</StatValue>
        </Stat>
      </StatGrid>

      <ChartGrid>
        <Card $pad>
          <h3 style={headStyle}><LabelWithHelp help={GRADE_CENTER_KPI_HELP.scoreDistribution}>Score distribution</LabelWithHelp></h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {summary.dist.map((d) => (
              <div
                key={d.band}
                style={{ display: "flex", alignItems: "center", gap: 14 }}
              >
                <span
                  className="tnum"
                  style={{
                    width: 64,
                    fontSize: 13,
                    fontWeight: 600,
                    color: theme.textMuted,
                  }}
                >
                  {d.band}
                </span>
                <div
                  style={{
                    flex: 1,
                    height: 28,
                    background: theme.surface3,
                    borderRadius: 8,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${(d.n / summary.maxN) * 100}%`,
                      height: "100%",
                      background: bandColor[d.key],
                      borderRadius: 8,
                      minWidth: d.n ? 8 : 0,
                      transition: "width .6s cubic-bezier(.3,.7,.2,1)",
                    }}
                  />
                </div>
                <span
                  className="tnum"
                  style={{ width: 24, textAlign: "right", fontWeight: 700 }}
                >
                  {d.n}
                </span>
              </div>
            ))}
          </div>
        </Card>

        <Card $pad>
          <h3 style={{ ...headStyle, marginBottom: 4 }}><LabelWithHelp help={GRADE_CENTER_KPI_HELP.highestScoring}>Highest average scoring learners</LabelWithHelp></h3>
          <p
            style={{
              margin: "0 0 12px",
              fontSize: 13,
              color: theme.textMuted,
            }}
          >
            Ranked by average score
          </p>
          <div>
            {summary.leaderboard.map((s, i) => (
              <div
                key={`${s.name}-${i}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 0",
                  borderBottom:
                    i < summary.leaderboard.length - 1
                      ? `1px solid ${theme.border}`
                      : "none",
                }}
              >
                <span
                  className="tnum"
                  style={{
                    width: 18,
                    fontWeight: 800,
                    color: i < 3 ? theme.accentStrong : theme.textSubtle,
                    fontSize: 13,
                  }}
                >
                  {i + 1}
                </span>
                <Av
                  style={{
                    width: 30,
                    height: 30,
                    background: LEADER_COLORS[i % LEADER_COLORS.length],
                  }}
                >
                  {toInitials(s.name)}
                </Av>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <CellPrimary style={{ fontSize: 13 }}>{s.name}</CellPrimary>
                  <CellSub>{s.courseName}</CellSub>
                </div>
                <Tag $tone="accent" className="tnum">
                  {Math.round(s.avg)}%
                </Tag>
              </div>
            ))}
          </div>
        </Card>
      </ChartGrid>
    </div>
  );
}

const TAB_RESULTS = 'results';
const TAB_ANALYSIS = 'analysis';

const Grids = ({ selectedTitle }) => {
  const theme = useTheme();
  const apiUrl = process.env.REACT_APP_API_URL;
  // Results = the scores table; Full score analysis = the assessment dashboard.
  const [activeTab, setActiveTab] = useState(TAB_RESULTS);
  const [tableData, setTableData] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(initialValue);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [userId, setUserId] = useState(localStorage.getItem("userId"));
  const [selectedRows, setSelectedRows] = useState([]);
  const [scoreSortBy, setScoreSortBy] = useState(SCORES_LIST_DEFAULTS.sortBy);
  const [scoreSortDir, setScoreSortDir] = useState(SCORES_LIST_DEFAULTS.sortDir);
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogProps, setDialogProps] = useState({});
  const [selectedResultIds, setSelectedResultIds] = useState([]);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  const [selectedStudents, setSelectedStudents] = useState([]);
  const { selectedCourseId, selectedCourseName } = useCourseFilter();
  const { guardAction, lockProps, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();

  const scopeLabel = selectedCourseName || "All courses";

  useEffect(() => {
    const userIdFromStorage = localStorage.getItem("userId");
    setUserId(userIdFromStorage);
  }, []);

  useEffect(() => {
    if (userId) {
      getUsers(selectedCourseId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, selectedCourseId]);

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };

const getUsers = async (courseId = '') => {
  if (!userId) return;

  setLoading(true);
  setFetchError("");

  setTableData([]);
  setSelectedRows([]);
  setSelectedResultIds([]);

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), SCORES_FETCH_TIMEOUT_MS);
  let timedOut = false;

  try {
    const response = await fetch(
      buildScoresListUrl(apiUrl, userId, { courseId }),
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
          Authorization: getBasicAuthHeader(),
        },
        signal: controller.signal,
      }
    );

    const data = await parseScoresListResponse(response);
    setTableData([...data]);
  } catch (error) {
    if (error?.name === "AbortError") {
      timedOut = true;
    }
    console.error("Error fetching data:", error);
    setTableData([]);
    setFetchError(getScoreListFetchErrorMessage(error, { timedOut }));
  } finally {
    window.clearTimeout(timeoutId);
    setLoading(false);
  }
};

//console.log(tableData)

  const handleClose = () => {
    setOpen(false);
    setFormData(initialValue);
  };

  const onChange = (e) => {
    const { value, id } = e.target;
    setFormData({ ...formData, [id]: value });
  };

  const handlePrintCourse = (data) => {
    if (!guardAction('action.score.viewAnalytics')) return;
    const courseId = data.courseId;
    const studentId = data.userId;
    setDialogProps({ studentId, courseId, type: "course" });
    setDialogOpen(true);
  };

  const handlePrintAssessment = (data) => {
    if (!guardAction('action.score.viewAnalytics')) return;
    const testId = data.testId;
    const studentId = data.userId;
    setDialogProps({ studentId, testId, type: "test" });
    setDialogOpen(true);
  };

  // Students for the student filter dropdown (scoped to current course when selected).
  const courseSpecificStudents = useMemo(() => {
    const scoped = selectedCourseId
      ? tableData.filter((d) => d.courseId === selectedCourseId)
      : tableData;
    return [
      ...new Set(
        scoped.map((d) => d.firstName + " " + d.lastName)
      ),
    ];
  }, [tableData, selectedCourseId]);

  // 🔽 Reset dependent dropdowns when course changes
  useEffect(() => {
    setSelectedStudents([]);
  }, [selectedCourseId]);

  const filteredData = useMemo(() => {
  const filtered = tableData.filter((item) => {
    const studentName =
      item.firstName + " " + item.lastName;

    const studentMatch =
      selectedStudents.length === 0 ||
      selectedStudents.includes(studentName);

    return studentMatch;
  });

  const grouped = {};

  filtered.forEach((item) => {
    const key = `${item.userId}_${item.courseId}`;

    if (!grouped[key]) {
      grouped[key] = {
        resultId: item.resultId,
        userId: item.userId,
        courseId: item.courseId,
        courseName: item.courseName,
        firstName: item.firstName,
        lastName: item.lastName,
        assessments: [],
      };
    }

    const percentage =
      item.totalMarks > 0
        ? (item.marksScored / item.totalMarks) * 100
        : 0;

    // ✅ create assessment history container
    if (!grouped[key]._assessmentHistory) {
      grouped[key]._assessmentHistory = {};
    }

    if (!grouped[key]._assessmentHistory[item.testName]) {
      grouped[key]._assessmentHistory[item.testName] = [];
    }

    // ✅ push all attempts
    grouped[key]._assessmentHistory[item.testName].push({
      marksScored: item.marksScored,
      totalMarks: item.totalMarks,
      percentage,
      createdAt: item.createdAt,
    });
  });

  // ✅ keep latest entry only
  Object.values(grouped).forEach((student) => {

    Object.entries(student._assessmentHistory).forEach(
      ([testName, attempts]) => {

        // latest attempt based on createdAt
        const latestAttempt = attempts.sort(
          (a, b) =>
            new Date(b.createdAt) -
            new Date(a.createdAt)
        )[0];

        const safeField = testName.trim();

student[safeField] = {
  ...latestAttempt,
  attempts,
};

        student.assessments.push({
          testName,
          ...latestAttempt,
        });
      }
    );
  });

  return Object.values(grouped);
}, [tableData, selectedStudents]);

  // Derived, display-only KPIs / distribution / leaderboard for the summary band.
  const summary = useMemo(() => buildGradeSummary(filteredData), [filteredData]);

  const sortedScoreRows = useMemo(
    () => sortScoresLocally(filteredData || [], scoreSortBy, scoreSortDir),
    [filteredData, scoreSortBy, scoreSortDir],
  );

  const handleScoreSortChange = (columnKey) => {
    const next = getNextScoresSort(columnKey, scoreSortBy, scoreSortDir);
    setScoreSortBy(next.sortBy);
    setScoreSortDir(next.sortDir);
  };

  const scoreTableColumns = useMemo(() => {
    if (loading) {
      return [
        ...(!selectedCourseId
          ? [{ header: "Course", render: () => null, muted: true }]
          : []),
        ...SCORE_LOADING_TABLE_COLUMNS,
      ];
    }

    const assessmentNames = [
      ...new Set(
        (tableData || []).map((item) => item.testName?.trim()).filter(Boolean)
      ),
    ];

    return [
      ...(!selectedCourseId
        ? [{
            header: "Course",
            sortKey: SCORES_SORT_FIELDS.courseName,
            render: (r) => r.courseName || "—",
          }]
        : []),
      ...assessmentNames.map((name) => ({
        header: name,
        sortKey: name,
        mono: true,
        render: (r) => {
          const value = r[name];
          if (!value) return <span style={{ color: theme.textSubtle, fontSize: 12 }}>-</span>;
          const orderedAttempts = [...value.attempts].sort(
            (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
          );
          return (
            <Tooltip
              arrow
              title={(
                <div>
                  <div style={{ fontWeight: "bold", marginBottom: 6 }}>
                    Attempts: {orderedAttempts.length}
                  </div>
                  {orderedAttempts.map((attempt, index) => (
                    <div key={index}>
                      Attempt {index + 1}: {attempt.percentage.toFixed(1)}%
                    </div>
                  ))}
                </div>
              )}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                {value.percentage.toFixed(1)}%
              </div>
            </Tooltip>
          );
        },
      })),
    ];
  }, [loading, selectedCourseId, tableData, theme.textSubtle]);

  const submissionLabel = useMemo(() => {
    if (loading) return SCORE_LIST_MESSAGES.LOADING;
    if (fetchError) return fetchError;
    if (!summary) return "0 graded submissions";
    return `${summary.total} graded submission${summary.total === 1 ? "" : "s"}`;
  }, [loading, fetchError, summary]);

  // ================= EXPORT CSV (hero — all visible rows) =================

  const exportToCSV = () => {
    if (!guardAction('action.score.exportCsv')) return;
    const rows = filteredData || [];
    if (rows.length === 0) {
      alert("No scores to export");
      return;
    }

    const allAssessments = [
      ...new Set(
        rows.flatMap((row) => row.assessments.map((a) => a.testName))
      ),
    ];

    const escapeCell = (value) => {
      const text = String(value ?? "");
      if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
      return text;
    };

    const header = ["First Name", "Last Name", "Course", ...allAssessments];
    const body = rows.map((row) => {
      const assessmentMap = {};
      row.assessments.forEach((a) => {
        assessmentMap[a.testName] = `${a.percentage.toFixed(1)}%`;
      });
      return [
        row.firstName,
        row.lastName,
        row.courseName || scopeLabel,
        ...allAssessments.map((assessment) => assessmentMap[assessment] || "-"),
      ];
    });

    const csv = [header, ...body]
      .map((line) => line.map(escapeCell).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${scopeLabel}_scores.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // ================= EXPORT XLS =================

const exportToXLS = () => {
  if (!guardAction('action.score.exportXls')) return;
  if (selectedRows.length === 0) {
    alert("Please select rows");
    return;
  }

  // ✅ Get all unique assessments
  const allAssessments = [
    ...new Set(
      selectedRows.flatMap((row) =>
        row.assessments.map((a) => a.testName)
      )
    ),
  ];

  // ✅ Top rows
  const worksheetData = [];

  // Row 1
  worksheetData.push([
    `Course : ${scopeLabel} Result`,
  ]);

  // Row 2
  worksheetData.push([]);

  // Row 3 → Main Headers
  const mainHeaders = [
    "First Name",
    "Last Name",
    ...allAssessments,
  ];

  worksheetData.push(mainHeaders);

  // ✅ Student rows
  selectedRows.forEach((row) => {
    const assessmentMap = {};

    row.assessments.forEach((a) => {
      assessmentMap[a.testName] =
        `${a.percentage.toFixed(1)}%`;
    });

    const rowData = [
      row.firstName,
      row.lastName,
      ...allAssessments.map(
        (assessment) =>
          assessmentMap[assessment] || "-"
      ),
    ];

    worksheetData.push(rowData);
  });

  // ✅ Create worksheet
  const worksheet =
    XLSX.utils.aoa_to_sheet(worksheetData);

  // ✅ Column widths
  worksheet["!cols"] = [
    { wch: 20 },
    { wch: 20 },
    ...allAssessments.map(() => ({
      wch: 25,
    })),
  ];

  // ✅ Merge title row
  worksheet["!merges"] = [
    {
      s: { r: 0, c: 0 },
      e: {
        r: 0,
        c: allAssessments.length + 1,
      },
    },
  ];

  // ✅ Workbook
  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    "Results"
  );

  XLSX.writeFile(
    workbook,
    `${scopeLabel}_Results.xlsx`
  );
};

// ================= EXPORT PDF =================

const exportToPDF = () => {
  if (!guardAction('action.score.exportPdf')) return;
  if (selectedRows.length === 0) {
    alert("Please select rows");
    return;
  }

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a3", // ✅ Bigger page size
  });

  // ✅ Get all unique assessments
  const allAssessments = [
    ...new Set(
      selectedRows.flatMap((row) =>
        row.assessments.map((a) => a.testName)
      )
    ),
  ];

  // ✅ Title
  doc.setFontSize(16);

  doc.text(
    `Course : ${scopeLabel} Result`,
    14,
    15
  );

  // ✅ Table Headers
  const tableColumn = [
    "First Name",
    "Last Name",
    ...allAssessments,
  ];

  // ✅ Table Rows
  const tableRows = [];

  selectedRows.forEach((row) => {
    const assessmentMap = {};

    row.assessments.forEach((a) => {
      assessmentMap[a.testName] =
        `${a.percentage?.toFixed(1) || 0}%`;
    });

    const rowData = [
      row.firstName || "-",
      row.lastName || "-",
      ...allAssessments.map(
        (assessment) =>
          assessmentMap[assessment] || "-"
      ),
    ];

    tableRows.push(rowData);
  });

  // ✅ Dynamic column width
  const dynamicColumnStyles = {
    0: { cellWidth: 35 }, // First Name
    1: { cellWidth: 35 }, // Last Name
  };

  allAssessments.forEach((_, index) => {
    dynamicColumnStyles[index + 2] = {
      cellWidth: "wrap",
      minCellWidth: 25,
    };
  });

  // ✅ Generate Table
  autoTable(doc, {
    head: [tableColumn],
    body: tableRows,
    startY: 25,

    styles: {
      fontSize: 7,
      overflow: "linebreak",
      cellPadding: 2,
      valign: "middle",
      halign: "center",
    },

    headStyles: {
      fontStyle: "bold",
      fontSize: 8,
    },

    columnStyles: dynamicColumnStyles,

    margin: {
      left: 5,
      right: 5,
    },

    tableWidth: "wrap",

    // ✅ Automatically split wide tables across pages
    horizontalPageBreak: true,
    horizontalPageBreakRepeat: [0, 1], // Repeat First Name & Last Name

    // ✅ Prevent crash on large datasets
    rowPageBreak: "avoid",

    didDrawPage: (data) => {
      doc.setFontSize(10);

      doc.text(
        `Page ${doc.internal.getNumberOfPages()}`,
        doc.internal.pageSize.width - 30,
        10
      );
    },
  });

  // ✅ Save PDF
  doc.save(
    `${scopeLabel}_Results.pdf`
  );
};

  return (
    <PageShell>
      <ContentWrap>
        <SectionHead style={{ marginTop: 0 }}>
          <div>
            <h2>Scores &amp; results</h2>
            <p>{activeTab === TAB_RESULTS ? submissionLabel : 'Assessment-level analysis across your courses.'}</p>
          </div>
          {/* Exports act on the results table, so they only appear on that tab. */}
          {activeTab === TAB_RESULTS && (
          <PageActionsMenu
            menuTitle="Export results"
            menuSubtitle="Download filtered scores for reporting."
          >
            {(closeActions) => (
              <ActionsSection label="Export">
                <ActionsMenuItem
                  label="Export CSV"
                  description="Comma-separated spreadsheet"
                  icon={<Download size={15} strokeWidth={1.9} />}
                  disabled={!filteredData.length}
                  closeActions={closeActions}
                  onClick={exportToCSV}
                  {...lockProps('action.score.exportCsv')}
                />
                <ActionsMenuItem
                  label="Export XLS"
                  description="Excel workbook download"
                  icon={<FileSpreadsheet size={15} strokeWidth={1.9} />}
                  disabled={!filteredData.length}
                  closeActions={closeActions}
                  onClick={exportToXLS}
                  {...lockProps('action.score.exportXls')}
                />
                <ActionsMenuItem
                  label="Export PDF"
                  description="Print-ready results report"
                  icon={<FileText size={15} strokeWidth={1.9} />}
                  disabled={!filteredData.length}
                  closeActions={closeActions}
                  onClick={exportToPDF}
                  {...lockProps('action.score.exportPdf')}
                />
              </ActionsSection>
            )}
          </PageActionsMenu>
          )}
        </SectionHead>

        <SetTabs style={{ marginBottom: 16 }}>
          <SetTab $on={activeTab === TAB_RESULTS} onClick={() => setActiveTab(TAB_RESULTS)}>
            Results
          </SetTab>
          <SetTab $on={activeTab === TAB_ANALYSIS} onClick={() => setActiveTab(TAB_ANALYSIS)}>
            Full score analysis
          </SetTab>
        </SetTabs>

        {activeTab === TAB_ANALYSIS && <AssessmentDashboard />}

        {activeTab === TAB_RESULTS && (
          <>
        {loading ? <GradeSummaryShimmer /> : summary && <GradeSummary summary={summary} />}

        <FilterRow>
            <FilterGroup>
              <FormControl size="small" sx={{ minWidth: 220, maxWidth: 280 }}>
                <InputLabel id="student-label">Learners</InputLabel>
                <Select
                  labelId="student-label"
                  multiple
                  value={selectedStudents}
                  label="Learners"
                  onChange={(e) => guardAction('action.score.filterLearners', () => setSelectedStudents(e.target.value))}
                  renderValue={(selected) => (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {selected.map((value) => (
                        <Chip key={value} $soft style={{ height: 22, fontSize: 11 }}>
                          {value}
                        </Chip>
                      ))}
                    </div>
                  )}
                  sx={{
                    borderRadius: theme.rMd,
                    "& .MuiOutlinedInput-root": {
                      minHeight: theme.controlH,
                      borderRadius: theme.rMd,
                      background: theme.surface,
                    },
                  }}
                >
                  {courseSpecificStudents.map((student, index) => (
                    <MenuItem key={index} value={student}>
                      {student}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </FilterGroup>
          </FilterRow>

        <TableWrap>
          <DataTable
            rows={sortedScoreRows}
            loading={loading}
            pageSize={10}
            rowKey={(r, i) => r.studentId ?? i}
            selectable
            onSelectionChange={setSelectedRows}
            sortBy={scoreSortBy}
            sortDir={scoreSortDir}
            onSortChange={handleScoreSortChange}
            search
            searchPlaceholder="Search participants…"
            searchKeys={['firstName', 'lastName']}
            primary={{
              header: 'Participant',
              sortKey: SCORES_SORT_FIELDS.participant,
              label: (r) => `${r.firstName || ''} ${r.lastName || ''}`.trim() || '—',
            }}
            columns={scoreTableColumns}
            emptyText={fetchError || "No scores yet."}
          />
        </TableWrap>
          </>
        )}
      </ContentWrap>

      <AnalyticsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        {...dialogProps}
      />
      <ReportDialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        resultIds={selectedResultIds}
      />
      <FormDialog
        open={open}
        handleClose={handleClose}
        data={formData}
        onChange={onChange}
      />
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </PageShell>
  );
};

export default Grids;
